import {
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  PointLight,
  SpotLight,
  Scene,
  type Light,
  type Texture,
  EquirectangularReflectionMapping,
} from 'three';
import { PMREMGenerator } from 'three';
import type { WebGLRenderer } from 'three';
import type { LightConfig, EnvironmentConfig } from './types';
import type { AssetLoader } from './AssetLoader';

export class LightingRig {
  private lights: Light[] = [];
  private envTexture: Texture | null = null;

  async setup(
    scene: Scene,
    renderer: WebGLRenderer,
    lights: LightConfig[],
    env: EnvironmentConfig | undefined,
    loader: AssetLoader,
  ): Promise<void> {
    for (const config of lights) {
      const light = this.createLight(config);
      scene.add(light);
      this.lights.push(light);
    }

    if (env?.type === 'hdri' && env.path) {
      const hdrTexture = await loader.loadEnvironmentMap(env.path);
      hdrTexture.mapping = EquirectangularReflectionMapping;
      const pmrem = new PMREMGenerator(renderer);
      pmrem.compileEquirectangularShader();
      this.envTexture = pmrem.fromEquirectangular(hdrTexture).texture;
      pmrem.dispose();
      hdrTexture.dispose();

      scene.environment = this.envTexture;
      if (env.background) {
        scene.background = this.envTexture;
      }
      scene.environmentIntensity = env.intensity ?? 1.0;
    }
  }

  private createLight(config: LightConfig): Light {
    let light: Light;

    switch (config.type) {
      case 'directional': {
        const dl = new DirectionalLight(config.color, config.intensity);
        if (config.position) dl.position.set(...config.position);
        if (config.castShadow) dl.castShadow = true;
        light = dl;
        break;
      }
      case 'point': {
        const pl = new PointLight(config.color, config.intensity);
        if (config.position) pl.position.set(...config.position);
        light = pl;
        break;
      }
      case 'spot': {
        const sl = new SpotLight(config.color, config.intensity);
        if (config.position) sl.position.set(...config.position);
        light = sl;
        break;
      }
      case 'hemisphere': {
        light = new HemisphereLight(config.color, config.groundColor ?? 0x000000, config.intensity);
        break;
      }
      case 'ambient':
      default:
        light = new AmbientLight(config.color, config.intensity);
        break;
    }

    return light;
  }

  async swapEnvironment(
    url: string | null,
    renderer: WebGLRenderer,
    scene: Scene,
    loader: AssetLoader,
    intensity?: number,
    asBackground?: boolean,
  ): Promise<void> {
    this.envTexture?.dispose();
    this.envTexture = null;

    if (!url) {
      scene.environment = null;
      scene.background = null;
      return;
    }

    const hdrTexture = await loader.loadEnvironmentMap(url);
    hdrTexture.mapping = EquirectangularReflectionMapping;
    const pmrem = new PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    this.envTexture = pmrem.fromEquirectangular(hdrTexture).texture;
    pmrem.dispose();
    hdrTexture.dispose();

    scene.environment = this.envTexture;
    if (asBackground) {
      scene.background = this.envTexture;
    } else {
      scene.background = null;
    }
    if (intensity !== undefined) {
      scene.environmentIntensity = intensity;
    }
  }

  dispose(): void {
    for (const light of this.lights) {
      light.removeFromParent();
      if ('dispose' in light && typeof light.dispose === 'function') {
        light.dispose();
      }
    }
    this.lights = [];
    this.envTexture?.dispose();
    this.envTexture = null;
  }
}
